import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'atoms/Snackbar';
import Modal from 'molecules/Modal';
import {
  ModalActions,
  ModalContent,
  ModalSubmitButton,
} from 'molecules/Modal/Modal.styles';
import { useBaoSelector } from 'utils/hooks/redux';
import { AxiosError } from 'axios';
import { neutrals } from 'utils/styles/color';
import { createRevolutPaymentSetup, RevolutSetupResponse } from './api';
import { loadRevolutScript } from 'utils/helpers/loadRevolutScript';
import { usePaymentMethodPolling } from './usePaymentMethodPolling';

/* ─── Revolut Checkout SDK type contracts ─── */

/**
 * RevolutCheckout(token, mode) → checkout instance (for card fields, popups)
 * RevolutCheckout.payments({ publicToken }) → { revolutPay } (for Revolut Pay button)
 */

type RevolutCardFieldOptions = {
  target: HTMLElement;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onValidation?: (errors: Array<{ type: string; message: string }>) => void;
  onStatusChange?: (status: {
    completed: boolean;
    focused: boolean;
    empty: boolean;
  }) => void;
  styles?: Record<string, unknown>;
};

type RevolutCardField = {
  submit: (options?: {
    name?: string;
    email?: string;
    savePaymentMethodFor?: 'merchant' | 'customer';
  }) => void;
  validate?: () => Array<{ type: string; message: string }>;
  destroy?: () => void;
};

type RevolutPayMountOptions = {
  createOrder: () => Promise<{ publicId: string }>;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onCancel?: () => void;
  savePaymentMethodForMerchant?: boolean;
  buttonStyle?: Record<string, unknown>;
};

/** Instance from RevolutCheckout(token, mode) — card payments */
type RevolutCheckoutInstance = {
  createCardField?: (options: RevolutCardFieldOptions) => RevolutCardField;
  destroy?: () => void;
};

/** Instance from RevolutCheckout.payments({ publicToken }) — Revolut Pay */
type RevolutPayInstance = {
  mount: (target: HTMLElement, options: RevolutPayMountOptions) => void;
  destroy: () => void;
};

type RevolutPaymentsResult = {
  revolutPay: RevolutPayInstance;
};
export type RevolutSelectedMethod = 'bank_card' | 'revolut_pay';

type RevolutPaymentMethodModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedMethod?: RevolutSelectedMethod;
};

const RevolutPaymentMethodModal = ({
  open,
  onClose,
  onSuccess,
  selectedMethod = 'revolut_pay',
}: RevolutPaymentMethodModalProps) => {
  const { currentCompany } = useBaoSelector((state) => state.common);
  const user = useBaoSelector((state) => state.user.value);
  const { showSnackbar } = useSnackbar();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<RevolutCheckoutInstance | null>(null);
  const cardFieldRef = useRef<RevolutCardField | null>(null);
  const revolutPayRef = useRef<RevolutPayInstance | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitializingRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);

  const { paymentMethod: polledMethod } = usePaymentMethodPolling({
    paymentMethodId,
    enabled: isProcessing && !!paymentMethodId,
  });

  const isChallenged =
    isProcessing && polledMethod?.authentication_status === 'challenged';

  const clearAbortController = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const cleanupWidget = useCallback(() => {
    try {
      cardFieldRef.current?.destroy?.();
    } catch {
      /* noop */
    }
    try {
      revolutPayRef.current?.destroy?.();
    } catch {
      /* noop */
    }
    try {
      checkoutRef.current?.destroy?.();
    } catch {
      /* noop */
    }
    cardFieldRef.current = null;
    revolutPayRef.current = null;
    checkoutRef.current = null;
    setIsWidgetReady(false);
    setIsCardComplete(false);
  }, []);

  const resolveError = useCallback((error: unknown): string => {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return 'Request cancelled';
    }

    if ((error as AxiosError)?.isAxiosError) {
      const axiosError = error as AxiosError<{
        error?: string;
        message?: string;
      }>;
      const status = axiosError.response?.status;
      const backendMessage =
        axiosError.response?.data?.error || axiosError.response?.data?.message;

      if (status === 401 || status === 403) {
        return 'Your session has expired or you are not authorized. Please sign in again.';
      }

      if (backendMessage) {
        return backendMessage;
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Unable to initialize Revolut payment setup. Please try again.';
  }, []);

  const handleWidgetSuccess = useCallback(() => {
    setIsProcessing(false);
    showSnackbar(
      'Payment method saved, an will appear in your account.',
      'success'
    );
    onSuccess();
    onClose();
  }, [onClose, onSuccess, showSnackbar]);

  const handleWidgetCancel = useCallback(() => {
    setIsProcessing(false);
    setCancelled(true);
  }, []);

  const handleSubmitCard = useCallback(() => {
    if (!cardFieldRef.current) {
      setErrorMessage('Card widget is not ready. Please wait or retry.');
      return;
    }

    // Validate before submitting
    if (typeof cardFieldRef.current.validate === 'function') {
      const errors = cardFieldRef.current.validate();
      if (errors && errors.length > 0) {
        setErrorMessage(errors.map((e) => e.message).join('. '));
        return;
      }
    }

    const name =
      `${user.name || ''} ${user.surname || ''}`.trim() ||
      user.email ||
      'Cardholder';

    setIsProcessing(true);
    setErrorMessage(null);

    try {
      cardFieldRef.current.submit({
        name,
        email: user.email,
        savePaymentMethodFor: 'merchant',
      });
      // Results come via onSuccess / onError / onValidation callbacks
    } catch (error) {
      setIsProcessing(false);
      setErrorMessage(resolveError(error));
    }
  }, [resolveError, user.email, user.name, user.surname]);

  const mountWidget = useCallback(
    async (setup: RevolutSetupResponse) => {
      if (!containerRef.current) {
        return;
      }

      const RC = window.RevolutCheckout;

      if (!RC) {
        throw new Error('Revolut SDK is not loaded');
      }

      const env = setup.mode === 'prod' ? 'prod' : 'sandbox';

      if (selectedMethod === 'bank_card') {
        // Card flow: RevolutCheckout(token, mode) → createCardField
        const checkout = (await Promise.resolve(
          RC(setup.token, env)
        )) as RevolutCheckoutInstance;

        checkoutRef.current = checkout;

        if (!checkout.createCardField) {
          throw new Error(
            'Card form is not available in the current SDK version'
          );
        }

        const card = checkout.createCardField({
          target: containerRef.current,
          onSuccess: handleWidgetSuccess,
          onError: (error: unknown) => {
            setIsProcessing(false);
            setErrorMessage(resolveError(error));
          },
          onValidation: (errors: Array<{ type: string; message: string }>) => {
            // setIsProcessing(false);
            setIsCardComplete(errors.length === 0);
            if (errors.length > 0) {
              setErrorMessage(errors.map((e) => e.message).join('. '));
            } else {
              setErrorMessage(null);
            }
          },
          onStatusChange: (status) => {
            setIsCardComplete(status.completed);
            if (status.completed) {
              setErrorMessage(null);
            }
          },
        });

        cardFieldRef.current = card;
        setIsWidgetReady(true);
        return;
      }

      // Revolut Pay flow: RevolutCheckout.payments({ publicToken }) → revolutPay.mount()
      if (!RC.payments) {
        throw new Error(
          'RevolutCheckout.payments is not available in the current SDK version'
        );
      }

      // TODO: remove hardcoded key once backend returns publicKey
      const publicToken =
        setup.publicKey || (process.env.REACT_APP_REVOLUT_PUBLIC_KEY as string);

      const payments = (await RC.payments({
        locale: 'en',
        publicToken,
        mode: env,
      })) as RevolutPaymentsResult;

      if (!payments.revolutPay) {
        throw new Error(
          'Revolut Pay is not available in the current SDK version'
        );
      }

      revolutPayRef.current = payments.revolutPay;

      payments.revolutPay.mount(containerRef.current, {
        createOrder: async () => ({ publicId: setup.orderId || setup.token }),
        onSuccess: handleWidgetSuccess,
        onError: (error: unknown) => {
          setIsProcessing(false);
          setErrorMessage(resolveError(error));
        },
        onCancel: handleWidgetCancel,
        savePaymentMethodForMerchant: true,
      });

      setIsWidgetReady(true);
    },
    [handleWidgetCancel, handleWidgetSuccess, resolveError, selectedMethod]
  );

  const initialize = useCallback(async () => {
    if (!open || isInitializingRef.current) {
      return;
    }

    if (!currentCompany?.company_hs_id) {
      setErrorMessage('Company was not found. Please refresh and try again.');
      return;
    }

    isInitializingRef.current = true;
    setIsLoading(true);
    setIsProcessing(false);
    setCancelled(false);
    setErrorMessage(null);
    cleanupWidget();
    clearAbortController();

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const setup = await createRevolutPaymentSetup({
        companyHsId: currentCompany.company_hs_id,
        cancelUrl: window.location.href,
        successUrl: window.location.href,
        signal: abortController.signal,
      });

      if (abortController.signal.aborted) {
        return;
      }

      await loadRevolutScript();

      if (abortController.signal.aborted) {
        return;
      }

      if (setup.payment_method_id) {
        setPaymentMethodId(setup.payment_method_id);
      }

      await mountWidget(setup);
    } catch (error) {
      const message = resolveError(error);

      if (message !== 'Request cancelled') {
        setErrorMessage(message);
      }

      setIsProcessing(false);
    } finally {
      setIsLoading(false);
      isInitializingRef.current = false;
    }
  }, [
    cleanupWidget,
    clearAbortController,
    currentCompany,
    mountWidget,
    open,
    resolveError,
  ]);

  useEffect(() => {
    if (open) {
      initialize();
      return;
    }

    clearAbortController();
    cleanupWidget();
    setCancelled(false);
    setErrorMessage(null);
    setIsLoading(false);
    setIsProcessing(false);
    setPaymentMethodId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(
    () => () => {
      clearAbortController();
      cleanupWidget();
    },
    [cleanupWidget, clearAbortController]
  );

  return (
    <Modal
      open={open}
      maxWidth="sm"
      title="Add Revolut payment method"
      subtitle={
        selectedMethod === 'bank_card'
          ? 'Complete card setup using Revolut widget.'
          : 'Complete Revolut Pay setup using Revolut widget.'
      }
      onClose={isProcessing ? undefined : onClose}
    >
      <>
        <ModalContent>
          <Box
            sx={{
              p: '0 24px 12px 24px',
              width: '100%',
              maxWidth: '100%',
              overflowX: 'hidden',
              boxSizing: 'border-box',
              '.MuiAlert-root': {
                width: 'auto !important',
                minWidth: 'auto !important',
              },
            }}
          >
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 220,
                  gap: '12px',
                }}
              >
                <CircularProgress size={20} />
                <Typography fontSize="14px">
                  Preparing Revolut widget...
                </Typography>
              </Box>
            )}

            {!isLoading && errorMessage && (
              <Alert
                severity="error"
                sx={{
                  mb: '12px',
                  //   width: '100%',
                  //   minWidth: '100%',
                  //   maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  '.MuiPaper-root': {
                    width: 'auto',
                    minWidth: 'auto',
                  },
                }}
              >
                {errorMessage}
              </Alert>
            )}

            {!isLoading && cancelled && !errorMessage && (
              <Alert
                severity="info"
                sx={{
                  mb: '12px',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  '.MuiPaper-root': {
                    width: 'auto',
                    minWidth: 'auto',
                  },
                }}
              >
                Payment setup was cancelled.
              </Alert>
            )}

            {isChallenged && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  py: '24px',
                }}
              >
                <CircularProgress size={32} sx={{ color: '#004C56' }} />
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  sx={{ textAlign: 'center' }}
                >
                  Please complete the authentication in your bank app.
                </Typography>
                <Typography
                  fontSize="12px"
                  sx={{ color: neutrals[500], textAlign: 'center' }}
                >
                  We&apos;re waiting for your bank to confirm...
                </Typography>
              </Box>
            )}

            {!errorMessage && !isChallenged && (
              <Box
                ref={containerRef}
                sx={
                  isWidgetReady
                    ? {
                        borderRadius: '8px',
                        position: 'relative',
                      }
                    : {
                        minHeight: selectedMethod === 'bank_card' ? 80 : 120,
                        border: `1px solid ${neutrals[200]}`,
                        borderRadius: '8px',
                        p: '12px',
                        position: 'relative',
                      }
                }
              >
                {!isWidgetReady && !isLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: neutrals.A200,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </ModalContent>

        <ModalActions>
          {selectedMethod === 'bank_card' && !errorMessage && !isChallenged && (
            <ModalSubmitButton
              size="medium"
              variant="contained"
              onClick={handleSubmitCard}
              disabled={
                isLoading || isProcessing || !isWidgetReady || !isCardComplete
              }
            >
              {isProcessing ? 'Processing...' : 'Save payment method'}
            </ModalSubmitButton>
          )}

          {!!errorMessage && (
            <ModalSubmitButton
              size="medium"
              variant="contained"
              onClick={initialize}
              disabled={isLoading}
            >
              Retry
            </ModalSubmitButton>
          )}
        </ModalActions>
      </>
    </Modal>
  );
};

export default RevolutPaymentMethodModal;
